<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatEur } from '@/composables/useSubscription'
import { useSheetScrollLock } from '@/composables/useSheetScrollLock'
import type { ResultDetail } from './ResultsStep.vue'

/**
 * The top-up offer screen (e.g. Tonic-Études mid-health-journey): a single
 * recommended product with a detail panel and a skip link. Presentational —
 * the parent supplies the resolved data and handles navigation via choose/skip.
 */

const props = defineProps<{
  name: string
  image: string
  /** Yearly price in euros, or null while the backend bundle is still loading. */
  priceYear: number | null
  detail: ResultDetail
  /** True on small screens: the detail panel becomes a teleported bottom sheet. */
  isMobileSheet?: boolean
}>()

defineEmits<{ choose: []; skip: [] }>()

const { t, locale } = useI18n()

const detailOpen = ref(false)

// On mobile, lock body scroll + close on Escape while the detail sheet is open.
useSheetScrollLock(
  computed(() => !!props.isMobileSheet && detailOpen.value),
  () => { detailOpen.value = false },
)

function formatPrice(value: number): string {
  return formatEur(value, locale.value)
}
</script>

<template>
  <div class="journey-topup">
    <h1 id="journey-question" class="journey-topup__title">
      {{ t('subscription.topUp.title') }}
    </h1>

    <article class="topup-card">
      <span class="topup-card__badge">
        <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true">
          <path d="M10 2l2.2 4.6 5 .6-3.7 3.4 1 5L10 13.7 5.5 15.6l1-5L2.8 7.2l5-.6z" fill="currentColor"/>
        </svg>
        {{ t('subscription.results.recommended') }}
      </span>

      <div class="topup-card__media">
        <img :src="image" :alt="t('subscription.topUp.imageAlt')" width="800" height="446" loading="lazy" />
      </div>

      <div class="topup-card__body">
        <p class="topup-card__category">{{ t('subscription.topUp.category') }}</p>
        <h2 class="topup-card__name">{{ name }}</h2>
        <p v-if="priceYear !== null" class="topup-card__price">
          <span class="topup-card__amount">{{ formatPrice(priceYear) }}</span>
          <span class="topup-card__period">{{ t('subscription.results.perYear') }}</span>
        </p>

        <div class="topup-card__actions">
          <button type="button" class="topup-card__choose" @click="$emit('choose')">
            {{ t('subscription.topUp.choose') }}
          </button>
          <button
            type="button"
            class="topup-card__more"
            :aria-expanded="detailOpen"
            aria-controls="journey-topup-detail"
            @click="detailOpen = !detailOpen"
          >
            <span>{{ detailOpen
              ? t('subscription.results.learnLess')
              : t('subscription.results.learnMore') }}</span>
            <svg
              class="topup-card__chevron"
              :class="{ 'is-open': detailOpen }"
              viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"
            >
              <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </article>

    <Teleport to="body" :disabled="!isMobileSheet">
      <div
        v-if="detailOpen"
        class="results-sheet"
        :class="{ 'results-sheet--mobile': isMobileSheet }"
      >
        <div class="results-sheet__backdrop" @click="detailOpen = false" />
        <div
          id="journey-topup-detail"
          class="results-detail"
          :role="isMobileSheet ? 'dialog' : undefined"
          :aria-modal="isMobileSheet ? 'true' : undefined"
        >
          <span class="results-detail__grabber" aria-hidden="true" />
          <button
            type="button"
            class="results-detail__close"
            :aria-label="t('subscription.results.learnLess')"
            @click="detailOpen = false"
          >
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <p class="results-detail__intro">👉 {{ t('subscription.results.inClear') }}</p>
          <p class="results-detail__summary">
            <strong>{{ name }}</strong> : {{ detail.summary }}
          </p>
          <dl class="results-detail__rows">
            <div v-for="row in detail.rows" :key="row.label" class="results-detail__row">
              <dt>{{ row.label }}</dt>
              <dd>{{ row.value }}</dd>
            </div>
          </dl>
          <!-- Same external-document handling as ResultsStep: a new tab with a
               severed opener, so the in-progress journey survives behind it. -->
          <a
            v-if="detail.sheetUrl"
            class="results-detail__sheet"
            :href="detail.sheetUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t('subscription.results.guaranteeSheet') }}
            <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
              <path d="M8 4H5v11h11v-3M12 3h5v5M17 3l-7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </Teleport>

    <button type="button" class="topup-skip" @click="$emit('skip')">
      {{ t('subscription.topUp.skip') }}
    </button>
  </div>
</template>

<style scoped>
.results-detail {
  position: relative;
  padding: clamp(1rem, 3vw, 1.75rem);
  padding-right: 3rem;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}
.results-detail__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--color-white);
  border: 1px solid var(--border);
  color: var(--color-chocolate);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out);
}
.results-detail__close:hover {
  background: color-mix(in oklab, var(--color-cherry) 14%, white);
  color: var(--color-cherry);
}
.results-detail__close:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.results-detail__intro {
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}
.results-detail__summary {
  margin-top: 0.35rem;
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--color-chocolate);
  max-width: 65ch;
}
.results-detail__rows {
  margin: 1rem 0 0;
  max-width: 44rem;
}
.results-detail__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid color-mix(in oklab, var(--border) 70%, transparent);
}
.results-detail__row dt {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
}
.results-detail__row dd {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
  text-align: right;
}

/* Calaméo guarantee sheet link, at the foot of the "En savoir +" panel. */
.results-detail__sheet {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 1rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.results-detail__sheet:hover {
  color: var(--color-chocolate);
}
.results-detail__sheet:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

/* ---- Bottom sheet mobile pour le détail « en savoir + » ----
 * Desktop : le wrapper est transparent (display:contents) → le panneau reste
 * affiché inline sous la grille, comme avant. Mobile : feuille glissante
 * ancrée en bas, par-dessus un voile, scroll interne (cf. isMobileSheet, 48rem). */
.results-sheet { display: contents; }
.results-sheet__backdrop,
.results-detail__grabber { display: none; }

.results-sheet--mobile {
  display: block;
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
}
.results-sheet--mobile .results-sheet__backdrop {
  display: block;
  position: absolute;
  inset: 0;
  background: color-mix(in oklab, var(--color-chocolate) 55%, transparent);
  animation: results-sheet-fade var(--dur-base) var(--ease-out);
}
.results-sheet--mobile .results-detail {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 85vh;
  overflow-y: auto;
  border: none;
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding-top: 1.25rem;
  padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.18);
  animation: results-sheet-slide var(--dur-base) var(--ease-out);
}
.results-sheet--mobile .results-detail__grabber {
  display: block;
  width: 2.5rem;
  height: 0.3rem;
  margin: 0 auto 0.85rem;
  border-radius: var(--radius-pill);
  background: color-mix(in oklab, var(--color-chocolate) 25%, transparent);
}
.results-sheet--mobile .results-detail__close {
  top: 1rem;
}
@keyframes results-sheet-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes results-sheet-slide {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .results-sheet--mobile .results-sheet__backdrop,
  .results-sheet--mobile .results-detail {
    animation: none;
  }
}

/* ---- Écran surcomplémentaire : Tonic-Études ---- */
.journey-topup {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.journey-topup__title {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}

.topup-card {
  position: relative;
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
  padding: 1.25rem;
  background: color-mix(in oklab, var(--color-peach) 10%, white);
  border: 1px solid var(--primary);
  border-radius: var(--radius-lg);
  box-shadow: 0 0 0 1px var(--primary), var(--shadow-sm);
}
@media (min-width: 38rem) {
  .topup-card {
    grid-template-columns: minmax(0, 18rem) minmax(0, 1fr);
    align-items: center;
  }
}

.topup-card__badge {
  position: absolute;
  top: -0.7rem;
  left: 1.25rem;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.7rem;
  background: var(--primary);
  color: var(--text-on-primary);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-sm);
}

.topup-card__media {
  overflow: hidden;
  border-radius: var(--radius-md);
  background: color-mix(in oklab, var(--color-peach) 16%, white);
}
.topup-card__media img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 800 / 446;
  object-fit: cover;
}

.topup-card__body {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}
.topup-card__category {
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}
.topup-card__name {
  font-size: var(--text-lg);
  font-weight: 800;
  color: var(--color-chocolate);
}
.topup-card__price {
  display: flex;
  align-items: baseline;
  gap: 0.1rem;
  padding-top: 0.35rem;
}
.topup-card__amount {
  font-size: var(--text-xl);
  font-weight: 800;
  color: var(--color-chocolate);
}
.topup-card__period {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
}
.topup-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  margin-top: 0.85rem;
}
.topup-card__choose {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.5rem;
  background: var(--primary);
  border: 1px solid var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-on-primary);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.topup-card__choose:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  transform: translateY(-1px);
}
.topup-card__choose:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.topup-card__more {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  background: none;
  border: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out);
}
.topup-card__more:hover { color: var(--color-chocolate); }
.topup-card__more:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}
.topup-card__chevron {
  flex-shrink: 0;
  transition: transform var(--dur-fast) var(--ease-out);
}
.topup-card__chevron.is-open { transform: rotate(180deg); }

.topup-skip {
  align-self: center;
  padding: 0.5rem 1rem;
  background: none;
  border: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out);
}
.topup-skip:hover { color: var(--color-chocolate); }
.topup-skip:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}

</style>
